import React, { useState } from "react";
import {
  CircularProgress,
  Box,
  Button,
  Typography,
  Container,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AuthInput from "../commponents/AuthInput.tsx";
import AppDialog from "../commponents/AppDialouge.tsx";
import { useDispatch } from "react-redux";
import { signIn } from "../services/auth/services.ts";
import { userActions } from "../redux/user/slice.ts";
import images from "../constant/images.ts";
import { isValidEmail } from "../utils/helpers.ts";
import { useNavigate } from "react-router-dom";

const userInitialData = {
  email: "",
  password: "",
};

const initialError = {
  errorEmail: "",
  errorPassword: "",
};

const SignIn = () => {
  const theme = useTheme();
  const [userData, setUserData] = useState(userInitialData);
  const [secureTextEntry, setSecureTextEntry] = useState(false);
  const [responseError, setResponseError] = useState("");
  const [inputError, setInputError] = useState(initialError);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleOnChange = (name: string, value: string) => {
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  const handleSubmit = () => {
    let isError = false;
    let errorText = { ...initialError };

    if (!isValidEmail(userData.email)) {
      errorText.errorEmail = "Email is Invalid";
      isError = true;
    }

    if (userData.password.length < 6) {
      errorText.errorPassword =
        "The password should have a minimum length of 6 characters";
      isError = true;
    }

    if (isError) {
      setInputError(errorText);
      return;
    } else {
      setLoading(true);
      signIn(userData.email, userData.password, {})
        .then((response) => {
          // Handle the response as before
          dispatch(
            userActions.setUser({
              customerName: response.data.customerName,
              customerId: response.data.customerId,
              isLoggedIn: true,
              customerEmail: response.data.customerEmail,
              phoneno: response.data.phoneno,
              accessToken: response.data.accessToken || "asdsad",
            })
          );
          navigate("/");
        })
        .catch((err) => {
          alert("The username or password is incorrect");
        })
        .finally(() => setLoading(false));
    }
  };

  // Styles object
  const styles = {
    container: {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      minHeight: "100vh",
      backgroundColor: "#f0f2f5",
    },
    gridContainer: {
      backgroundColor: "white",
      borderRadius: 8,
      boxShadow: 1,
      p: 4,
      width: "100%",
      maxWidth: 800, // Adjusted width to accommodate side-by-side layout
    },
    leftGrid: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 2,
    },
    logo: {
      width: 100,
      height: "auto", // Set height to auto to maintain aspect ratio
    },
    welcomeText: {
      fontWeight: "bold",
      letterSpacing: 1,
      marginBottom: 1,
      fontFamily: "Spectral, serif",
    },
    infoText: {
      fontFamily: "Spectral, serif",
      letterSpacing: 1,
    },
    rightGrid: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: 2,
    },
    authInput: {
      width: "100%",
      height: 48,
      mb: 2,
      borderRadius: 1,
    },
    submitButton: {
      width: "100%",
      height: 48,
      borderRadius: 1,
      backgroundColor: "#b7472a",
      "&:hover": {
        backgroundColor: "#8E2B1E",
      },
    },
    footerText: {
      textAlign: "center",
      mt: 2,
      fontFamily: "Spectral, serif",
    },
    forgotPasswordButton: {
      color: "#092238",
      fontWeight: "bold",
      fontFamily: "Spectral, serif",
      letterSpacing: 1,
    },
    createAccountButton: {
      letterSpacing: 1,
      backgroundColor: "grey",
      color: "white",
      fontWeight: "400",
      paddingX: "18px",
      fontFamily: "Spectral, serif",
      borderRadius: 1,
      "&:hover": {
        backgroundColor: "darkgrey",
      },
    },
  };

  return (

    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f4f6f8",
        py: 6,
      }}
    >
      <Grid
        container
        sx={{
          width: "100%",
          maxWidth: 900,
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: 4,
          overflow: "hidden",
        }}
      >
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            px: 5,
            py: 6,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Welcome Back
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
            Sign in to continue to your account
          </Typography>
  

          <AuthInput
            label="Email"
            onChange={(e) => handleOnChange("email", e.target.value)}
            value={userData.email}
            errorMessage={inputError.errorEmail}

          />
  

          <AuthInput
            label="Password"
            onChange={(e) => handleOnChange("password", e.target.value)}
            value={userData.password}
            secureTextEntry={!secureTextEntry}
            setSecureTextEntry={() => setSecureTextEntry(!secureTextEntry)}
            errorMessage={inputError.errorPassword}

          />
  
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ borderRadius: 2, py: 1.4, mt: 3 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Sign In"}
          </Button>
  
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2">
              Don’t have an account?{" "}
              <Button
                onClick={() => navigate("/signup")}
                sx={{ color: theme.palette.primary.main, fontWeight: 600, px: 0 }}
              >
                Sign up
              </Button>
            </Typography>
          </Box>
  
          <Box sx={{ mt: 1 }}>
            <Button
              onClick={() => navigate("/forgot-password")}
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 500,
                textTransform: "none",
              }}
            >
              Forgot your password?
            </Button>
          </Box>
        </Grid>
  
        {/* Right side - Logo */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            backgroundColor: "#f0f2f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
          }}
        >
          <img
            src={images.logo}
            alt="Logo"
            style={{ width: 200, height: "auto" }}
          />
        </Grid>
      </Grid>
 
      <AppDialog
        visible={!!responseError}
        onClose={() => setResponseError("")}
      />
    </Box>
  );
  
};

export default SignIn;
